import { CSSProperties, JSX, ReactNode } from 'react';

export enum TooltipPortalPosition {
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'bottom',
  LEFT = 'left',
}

export enum TooltipPortalContentDirection {
  LTR = 'ltr',
  CENTER = 'center',
  RTL = 'rtl',
}

export type ITooltipPortalProps = {
  id?: string;
  children?: ReactNode | ((props: { open: boolean; handleShow: () => void; handleHide: () => void }) => JSX.Element);
  /** the actual content in the tooltip */
  tooltipContent: JSX.Element;
  position?: TooltipPortalPosition;
  contentDirection?: TooltipPortalContentDirection;
  styles?: {
    backgroundColor?: CSSProperties['backgroundColor'];
    color?: CSSProperties['color'];
    width?: CSSProperties['width'];
    padding?: CSSProperties['padding'];
    borderRadius?: CSSProperties['borderRadius'];
    boxShadow?: CSSProperties['boxShadow'];
    fontSize?: CSSProperties['fontSize'];
    zIndex?: CSSProperties['zIndex'];
  };
  portalWrapperStyles?: React.CSSProperties;
  /**
   * A positive number to adjust the left/right tooltip tooltop position when using top/bottom position with ltr/rtl contentDirection.
   *
   * Useful when increasing the border-radius of the tooltip.
   *
   * Note: this is not used to "guess" the position. It shifts the tooltip content by the number of pixels provided while maintaining the correct tail position.
   * */
  horizontalOffset?: number;
  verticalOffset?: number;
  tooltipWrapperClass?: string;
  childrenWrapperClass?: string;
  contentWrapperClass?: string;
  portalHover?: (isHovered: boolean) => void;
  stopPropagation?: boolean;
  disabled?: boolean;
  delayStart?: number;
  /** tailSize is a number that is converted to px */
  tailSize?: number;
  /** tailHorizontalPosition is a percentage 0 - 100 to force the positinoing from left to right*/
  tailHorizontalPosition?: number;
  keepOpenOnContentHover?: boolean;
  animationType?: 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'fade';
  slideDisplacement?: number | string;
  /** only use open if you want the tooltip to be controlled */
  open?: boolean;
  applyActiveSvgFillColor?: boolean;
  /**
   * for showOnFocus to work, the children must have a tabbable element, e.g., a button
   *
   * ```
   * <TooltipPortal showOnFocus {...otherProps}>
   *  <button>...</button>
   * </TooltipPortal>
   * ```
   * */
  showOnFocus?: boolean;
  showOnHover?: boolean;
  alwaysCloseOnMouseOut?: boolean;
  closeOnEscape?: boolean;
  onTooltipClick?: () => void;
  onTriggerClick?: () => void;
  onClickAway?: (meta: { clickedTriggerElement: boolean }) => void;
  onEscape?: () => void;
  onTab?: () => void;
  onTriggerTabAway?: () => void;
  forceClose?: boolean;
};
